// Sharing one piece of work.
//
// A shared link has to land somebody on the thing that was shared, in the place
// it was shared from. The two places show the same pictures very differently:
// Explore centres one at full size, and the gallery is a wall of all of
// them. Somebody who shared the wall meant "look at this room", and somebody
// who shared the rail meant "look at this piece", so the link carries which.
//
// Both routes already read ?piece=, so there is no new URL shape here, only the
// origin the link is built against.
//
// The library works the same way, with ?sheet= and ?prompt= against /library:
// a character and the prompt behind it are each a thing somebody means to send
// on its own, and each opens where it is read rather than at the top of a list.

// Where each kind of thing lives, and anything the page has to be told beyond
// the id. The parameter is always the kind's own name, which is what both pages
// already read and what the edge function looks for when it builds a preview.
const ROUTES = {
  piece: { path: '/gallery/' },
  // The wall opens on the right tab for the kind, so a shared clip does not
  // land among the pictures and leave somebody hunting for it.
  clip: { path: '/gallery/', query: { tab: 'videos' } },
  sheet: { path: '/library/' },
  prompt: { path: '/library/' },
};

// Where a share made from this page should send somebody back to.
export function shareUrl({ kind, id }) {
  if (typeof window === 'undefined' || !id) return '';

  const key = ROUTES[kind] ? kind : 'piece';
  const route = ROUTES[key];
  const url = new URL(route.path, window.location.origin);

  for (const [name, value] of Object.entries(route.query || {})) {
    url.searchParams.set(name, value);
  }
  url.searchParams.set(key, id);

  return url.toString();
}

// Copies the link. Deliberately not navigator.share.
//
// The share sheet was tried and is worse for this: it hands the reader a page
// of applications to pick from when the thing they asked for was the address,
// and on a desktop it is a dialog in front of the work they were looking at.
// Copying is the whole of what was wanted, and it takes no decisions.
//
// Synchronous in practice, so the caller is free to decide how long to look
// busy for. Nothing here waits on anything.
export function copyLink({ kind, id }) {
  const url = shareUrl({ kind, id });
  if (!url) return 'failed';
  return copy(url) ? 'copied' : 'failed';
}

// Two ways, because the good one is not always there.
//
// navigator.clipboard exists only in a secure context, and http on a machine's
// own address is not one: reaching the site from a phone over the wifi, the
// whole API is undefined and every share answered "could not share". The
// deployed site is https and never sees this, which is exactly the sort of
// thing that only shows up once somebody tests on their own network.
//
// execCommand is deprecated and still works everywhere, including there.
function copy(text) {
  try {
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      // Deliberately not awaited. The caller wants to know a route was taken,
      // and a rejection here still leaves the fallback below untried, so the
      // promise is caught and quietly ignored rather than left dangling.
      navigator.clipboard.writeText(text).catch(() => legacyCopy(text));
      return true;
    }
  } catch {
    // Falls through.
  }

  return legacyCopy(text);
}

function legacyCopy(text) {
  try {
    const field = document.createElement('textarea');
    field.value = text;
    // Off screen rather than hidden: display:none and visibility:hidden are
    // both unselectable, and selection is what this depends on.
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.top = '-1000px';
    field.style.opacity = '0';

    document.body.appendChild(field);
    field.select();
    field.setSelectionRange(0, text.length);

    const ok = document.execCommand('copy');
    document.body.removeChild(field);
    return ok;
  } catch {
    return false;
  }
}
